import React, { useState } from 'react';
import { ArrowLeftIcon, SaveIcon, PlusIcon, TrashIcon, MoveIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function PlaysConfigurationPage() {
  const [playName, setPlayName] = useState('High-Intent Account Blitz');
  const [description, setDescription] = useState('Multi-channel outreach sequence targeting accounts showing 3+ buying signals within 7 days');
  const [trigger, setTrigger] = useState('3+ buying signals detected within 7 days');
  const [objective, setObjective] = useState('Book qualified meeting within 14 days');
  const [steps, setSteps] = useState([{
    id: 1,
    name: 'Intent signal detection',
    owner: 'Marketing Ops',
    slaHours: 2
  }, {
    id: 2,
    name: 'Account research',
    owner: 'SDR',
    slaHours: 4
  }, {
    id: 3,
    name: 'Multi-channel outreach',
    owner: 'SDR',
    slaHours: 24
  }, {
    id: 4,
    name: 'Meeting booking',
    owner: 'SDR',
    slaHours: 48
  }]);
  const [eligibility, setEligibility] = useState(['Enterprise segment', 'Tech vertical', 'North America']);
  const [guardrails, setGuardrails] = useState(['Max 3 touches per week', 'No weekend outreach', 'Approved messaging only']);
  const [stopRules, setStopRules] = useState(['Explicit opt-out', 'No response after 5 touches', 'Meeting booked']);
  const addStep = () => {
    setSteps([...steps, {
      id: steps.length + 1,
      name: '',
      owner: '',
      slaHours: 24
    }]);
  };
  const removeStep = (id: number) => {
    setSteps(steps.filter(step => step.id !== id));
  };
  const updateStep = (id: number, field: string, value: any) => {
    setSteps(steps.map(step => step.id === id ? {
      ...step,
      [field]: value
    } : step));
  };
  return <div className="w-full h-screen flex flex-col bg-gray-50" data-id="element-5786">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-5787">
        <div className="flex items-center justify-between" data-id="element-5788">
          <div className="flex items-center gap-4" data-id="element-5789">
            <Link to="/plays/detail" className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-5790">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-5791" />
            </Link>
            <div data-id="element-5792">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-5793">
                Configure Play
              </h1>
              <p className="text-sm text-gray-600 mt-1" data-id="element-5794">
                Set up triggers, steps, and rules for your play
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-5795">
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5796">
              Cancel
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors" data-id="element-5797">
              <SaveIcon className="w-4 h-4" data-id="element-5798" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6" data-id="element-5799">
        <div className="max-w-5xl mx-auto space-y-6" data-id="element-5800">
          {/* Basic Information */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5801">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5802">
              Basic Information
            </h3>
            <div className="space-y-4" data-id="element-5803">
              <div data-id="element-5804">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5805">
                  Play Name
                </label>
                <input type="text" value={playName} onChange={e => setPlayName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5806" />
              </div>
              <div data-id="element-5807">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5808">
                  Description
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none" data-id="element-5809" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-5810">
                <div data-id="element-5811">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5812">
                    Motion Type
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5813">
                    <option data-id="element-5814">ABM</option>
                    <option data-id="element-5815">PLG</option>
                    <option data-id="element-5816">Inbound</option>
                    <option data-id="element-5817">Outbound</option>
                  </select>
                </div>
                <div data-id="element-5818">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5819">
                    Stage
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5820">
                    <option data-id="element-5821">TOFU</option>
                    <option data-id="element-5822">MOFU</option>
                    <option data-id="element-5823">BOFU</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          {/* Trigger & Objective */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5824">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5825">
              Trigger & Objective
            </h3>
            <div className="space-y-4" data-id="element-5826">
              <div data-id="element-5827">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5828">
                  Trigger Condition
                </label>
                <input type="text" value={trigger} onChange={e => setTrigger(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" placeholder="e.g., 3+ buying signals detected within 7 days" data-id="element-5829" />
              </div>
              <div data-id="element-5830">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5831">
                  Play Objective
                </label>
                <input type="text" value={objective} onChange={e => setObjective(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" placeholder="e.g., Book qualified meeting within 14 days" data-id="element-5832" />
              </div>
            </div>
          </div>
          {/* Play Steps */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5833">
            <div className="flex items-center justify-between mb-4" data-id="element-5834">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-5835">
                Play Steps
              </h3>
              <button onClick={addStep} className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors" data-id="element-5836">
                <PlusIcon className="w-4 h-4" data-id="element-5837" />
                Add Step
              </button>
            </div>
            <div className="space-y-3" data-id="element-5838">
              {steps.map((step, index) => <div key={step.id} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-5839">
                  <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0" data-id="element-5840">
                    {index + 1}
                  </div>
                  <div className="flex-1 grid grid-cols-3 gap-3" data-id="element-5841">
                    <input type="text" value={step.name} onChange={e => updateStep(step.id, 'name', e.target.value)} placeholder="Step name" className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm" data-id="element-5842" />
                    <input type="text" value={step.owner} onChange={e => updateStep(step.id, 'owner', e.target.value)} placeholder="Owner" className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm" data-id="element-5843" />
                    <div className="flex items-center gap-2" data-id="element-5844">
                      <input type="number" value={step.slaHours} onChange={e => updateStep(step.id, 'slaHours', parseInt(e.target.value))} placeholder="SLA" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm" data-id="element-5845" />
                      <span className="text-sm text-gray-600" data-id="element-5846">hours</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-5847">
                    <button className="p-2 text-gray-400 hover:text-gray-600" data-id="element-5848">
                      <MoveIcon className="w-4 h-4" data-id="element-5849" />
                    </button>
                    <button onClick={() => removeStep(step.id)} className="p-2 text-gray-400 hover:text-red-600" data-id="element-5850">
                      <TrashIcon className="w-4 h-4" data-id="element-5851" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Eligibility Criteria */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5852">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5853">
              Eligibility Criteria
            </h3>
            <div className="space-y-3" data-id="element-5854">
              {eligibility.map((criteria, index) => <div key={index} className="flex items-center gap-3" data-id="element-5855">
                  <input type="text" value={criteria} onChange={e => {
                const newEligibility = [...eligibility];
                newEligibility[index] = e.target.value;
                setEligibility(newEligibility);
              }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5856" />
                  <button onClick={() => {
                setEligibility(eligibility.filter((_, i) => i !== index));
              }} className="p-2 text-gray-400 hover:text-red-600" data-id="element-5857">
                    <TrashIcon className="w-4 h-4" data-id="element-5858" />
                  </button>
                </div>)}
              <button onClick={() => setEligibility([...eligibility, ''])} className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-5859">
                <PlusIcon className="w-4 h-4" data-id="element-5860" />
                Add Criteria
              </button>
            </div>
          </div>
          {/* Guardrails */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5861">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5862">
              Guardrails
            </h3>
            <div className="space-y-3" data-id="element-5863">
              {guardrails.map((guardrail, index) => <div key={index} className="flex items-center gap-3" data-id="element-5864">
                  <input type="text" value={guardrail} onChange={e => {
                const newGuardrails = [...guardrails];
                newGuardrails[index] = e.target.value;
                setGuardrails(newGuardrails);
              }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5865" />
                  <button onClick={() => {
                setGuardrails(guardrails.filter((_, i) => i !== index));
              }} className="p-2 text-gray-400 hover:text-red-600" data-id="element-5866">
                    <TrashIcon className="w-4 h-4" data-id="element-5867" />
                  </button>
                </div>)}
              <button onClick={() => setGuardrails([...guardrails, ''])} className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-5868">
                <PlusIcon className="w-4 h-4" data-id="element-5869" />
                Add Guardrail
              </button>
            </div>
          </div>
          {/* Stop Rules */}
          <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-5870">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5871">
              Stop Rules
            </h3>
            <div className="space-y-3" data-id="element-5872">
              {stopRules.map((rule, index) => <div key={index} className="flex items-center gap-3" data-id="element-5873">
                  <input type="text" value={rule} onChange={e => {
                const newStopRules = [...stopRules];
                newStopRules[index] = e.target.value;
                setStopRules(newStopRules);
              }} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5874" />
                  <button onClick={() => {
                setStopRules(stopRules.filter((_, i) => i !== index));
              }} className="p-2 text-gray-400 hover:text-red-600" data-id="element-5875">
                    <TrashIcon className="w-4 h-4" data-id="element-5876" />
                  </button>
                </div>)}
              <button onClick={() => setStopRules([...stopRules, ''])} className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-5877">
                <PlusIcon className="w-4 h-4" data-id="element-5878" />
                Add Stop Rule
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}